/**
 * SubmitButton — Layer-1 full-width submit CTA for auth pages.
 *
 * Distinct from ThemedButton:
 *   - Always full-width (w-full)
 *   - Taller (h-12 vs h-10)
 *   - Uses rounded-xl (vs rounded-[0.75rem])
 *   - Bold label, text-sm (matches ThemedButton size="full")
 *   - Optional trailing arrow icon
 *
 * @layer-1 — propagates to all features automatically
 */
import { cn } from "@/shared/lib/utils";

interface SubmitButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Trailing icon (e.g. ArrowRight). Rendered only when not loading. */
  trailingIcon?: React.ReactNode;
}

export function SubmitButton({
  trailingIcon,
  children,
  className,
  disabled,
  ...props
}: SubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className={cn(
        "w-full h-12 rounded-xl btn-primary-colors",
        "font-bold text-sm",
        "flex items-center justify-center gap-3 transition-all duration-200",
        "disabled:opacity-35 disabled:cursor-not-allowed",
        "outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
        className
      )}
      style={{
        boxShadow: "var(--shadow-button, 0 2px 12px rgba(26,25,23,0.18))",
        fontFamily: "'Public Sans', sans-serif",
      }}
      {...props}
    >
      <span>{children}</span>
      {trailingIcon}
    </button>
  );
}
